import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { AuthUser } from '../types'
import { clearSession, fetchMe, getStoredUser, getToken, unlockAdmin } from '../api'
import GameFx from './GameFx'
import AudioFx, { isMuted, toggleMute } from './AudioFx'

export default function Layout({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser())
  const [showAdmin, setShowAdmin] = useState(false)
  const [muted, setMuted] = useState(isMuted())
  const [adminPwd, setAdminPwd] = useState('')
  const [adminMsg, setAdminMsg] = useState('')
  const adminTimerRef = useRef<number | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!getToken()) {
      setUser(null)
      return
    }
    fetchMe().then((u) => setUser(u))
  }, [location.pathname])

  useEffect(() => {
    const onAuth = () => {
      if (!getToken()) setUser(null)
      else fetchMe().then(setUser)
    }
    window.addEventListener('auth-refresh', onAuth)
    return () => window.removeEventListener('auth-refresh', onAuth)
  }, [])

  const onLogout = useCallback(() => {
    clearSession()
    setUser(null)
    navigate('/')
  }, [navigate])

  useEffect(() => {
    if (user?.isUnlimited && showAdmin) setShowAdmin(false)
  }, [user?.isUnlimited, showAdmin])

  const closeAdminPanel = useCallback(() => {
    setShowAdmin(false)
    setAdminPwd('')
    setAdminMsg('')
    if (adminTimerRef.current) {
      window.clearTimeout(adminTimerRef.current)
      adminTimerRef.current = null
    }
  }, [])

  const onAdmin = useCallback(async () => {
    setAdminMsg('')
    try {
      const next = await unlockAdmin(adminPwd)
      setUser(next)
      closeAdminPanel()
    } catch (err) {
      setAdminMsg(err instanceof Error ? err.message : '解锁失败')
      window.setTimeout(() => closeAdminPanel(), 1200)
    }
  }, [adminPwd, closeAdminPanel])

  return (
    <>
      <GameFx />
      <AudioFx />
      <div className="bg-ornament" aria-hidden>
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="wave" />
      </div>
      <header className="site-header">
        <div className="container inner">
          <NavLink to="/" className="brand">
            <img className="brand-mark-img" src="/logo-pad-v2.png?v=2" alt="游戏手柄 logo" />
            <span>AI GAME DESIGN</span>
          </NavLink>
          <nav className="nav">
            <NavLink to="/" end>
              首页
            </NavLink>
            <NavLink to="/create">开始策划</NavLink>
            <NavLink to="/history">我的记录</NavLink>
            {user ? (
              <>
                <span className="user-chip">
                  {user.isUnlimited ? (
                    <span className="quota unlimited">UNLIMITED</span>
                  ) : (
                    <>
                      <span>{user.username}</span>
                      <span className="quota">剩余 {user.remaining ?? 0}/{user.generateQuota}</span>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="linkish"
                  onClick={() => {
                    if (user?.isUnlimited) return
                    setShowAdmin((v) => {
                      const next = !v
                      if (next) {
                        setAdminMsg('')
                        setAdminPwd('')
                        if (adminTimerRef.current) window.clearTimeout(adminTimerRef.current)
                        adminTimerRef.current = window.setTimeout(() => closeAdminPanel(), 3000)
                      } else if (adminTimerRef.current) {
                        window.clearTimeout(adminTimerRef.current)
                        adminTimerRef.current = null
                      }
                      return next
                    })
                  }}
                >
                  管理员
                </button>
                <button type="button" className="linkish" onClick={onLogout}>
                  退出
                </button>
                <button
                  type="button"
                  className="linkish"
                  onClick={() => setMuted(toggleMute())}
                  title={muted ? '开启声音' : '静音'}
                >
                  {muted ? '🔇' : '🔊'}
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login">登录 / 注册</NavLink>
                <button
                  type="button"
                  className="linkish"
                  onClick={() => setMuted(toggleMute())}
                  title={muted ? '开启声音' : '静音'}
                >
                  {muted ? '🔇' : '🔊'}
                </button>
              </>
            )}
          </nav>
        </div>
        {user && showAdmin && !user.isUnlimited ? (
          <div className="container" style={{ paddingBottom: '0.85rem' }}>
            <div className="admin-box">
              <h3>管理员解锁 · 无限次生成</h3>
              <div style={{ display: 'flex', gap: '0.55rem', flexWrap: 'wrap' }}>
                <input
                  type="password"
                  value={adminPwd}
                  onChange={(e) => {
                    setAdminPwd(e.target.value)
                    if (adminTimerRef.current) window.clearTimeout(adminTimerRef.current)
                    adminTimerRef.current = window.setTimeout(() => closeAdminPanel(), 3000)
                  }}
                  placeholder="输入管理员密码"
                  style={{
                    flex: '1 1 220px',
                    border: '3px solid var(--border)',
                    background: 'var(--bg)',
                    padding: '0.55rem 0.75rem',
                  }}
                />
                <button type="button" className="btn btn-warm" onClick={onAdmin}>
                  解锁
                </button>
              </div>
              {adminMsg ? <p className="muted" style={{ marginBottom: 0 }}>{adminMsg}</p> : null}
            </div>
          </div>
        ) : null}
      </header>
      <main className="container">{children}</main>
      <footer className="footer">
        <div className="container">
          注册后即可生成 · 每人默认 3 次 · 管理员密码可解锁无限次 · 数据按用户隔离保存
        </div>
      </footer>
    </>
  )
}
